import JsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { shortSchoolarYearName } from "@/utilities/settings";
import {
  formatCurrency,
  getBuildingCurrency,
} from "@/utilities/countrySpecific";

const reverseIfArabic = (arr, isArabic) =>
  isArabic ? arr.slice().reverse() : arr;

const TYPE_COLORS = {
  income: [232, 245, 233],
  depense: [255, 241, 240],
  inverseIncome: [230, 247, 247],
  avoirCredit: [237, 240, 252],
};

const TYPE_LABEL_KEYS = {
  income: "caisse.typeEncaissement",
  depense: "caisse.typeDecaissement",
  inverseIncome: "caisse.typeInverseEncaissement",
  avoirCredit: "caisse.typeAvoirCredit",
};

const INCOME_TYPES = { income: true, inverseIncome: true, avoirCredit: true };

const loadLogo = (settings) => {
  const images = (settings && settings.image) || [];
  const match = images.find((img) => img.db === settings.activeBuilding);
  if (!match || !match.logo) return null;
  const img = new Image();
  img.src = match.logo;
  return img;
};

const rowTimestamp = (row) => {
  const ts = (v) => {
    if (v == null || v === "") return 0;
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : 0;
  };
  if (row._type === "income") {
    const d = ts(row.date);
    const c = ts(row.createdAt);
    return d && c ? Math.max(d, c) : d || c;
  }
  if (row._type === "inverseIncome" || row._type === "avoirCredit") {
    const r = ts(row.receivedAt);
    const c = ts(row.createdAt);
    return r && c ? Math.max(r, c) : r || c;
  }
  const p = ts(row.paidAt);
  const co = ts(row.createdAt);
  return p && co ? Math.max(p, co) : p || co;
};

const formatRowDate = (row) => {
  const ms = rowTimestamp(row);
  return ms ? moment(ms).format("DD/MM/YYYY HH:mm") : "--";
};

const typeLabel = (row, t) =>
  t(TYPE_LABEL_KEYS[row._type] || "caisse.typeDecaissement");

const schoolarYearLabel = (row, names) => {
  const id = row._schoolarYear || row.schoolarYear;
  const raw = names[id] || names[String(id)] || "";
  return shortSchoolarYearName(raw) || "--";
};

const serialOf = (row) => {
  if (row._type === "income") return row.serial || "--";
  return row.receiptNumber || "--";
};

const doneByOf = (row) => {
  if (row._type === "income") return (row.doneBy && row.doneBy.userName) || "--";
  if (row._type === "inverseIncome" || row._type === "avoirCredit") {
    return row.receivedBy || row.createdBy || "--";
  }
  return row.createdBy || "--";
};

const amountOf = (row) => {
  if (row._type === "income") return Number(row.montantTotal || 0);
  return Number(
    row._amountForCaisse != null
      ? row._amountForCaisse
      : row.totalReceived || row.totalToPay || row.amount || 0
  );
};

const isIncomeType = (type) => Boolean(INCOME_TYPES[type]);

const isCancelledInvoice = (row) =>
  row._type === "income" &&
  (row.status === "cancelled" || Boolean(row.cancellation && row.cancellation.type));

const feeKindLabel = (detail, t) => {
  if (detail.typeAS === "A") return t("encaissement.activity");
  if (detail.typeAS === "S") return t("encaissement.schooling");
  const text = String(detail.typeReglement || "");
  if (/activit/i.test(text)) return t("encaissement.activity");
  if (/scolar/i.test(text)) return t("encaissement.schooling");
  return text || "";
};

const descriptionOf = (row, t) => {
  if (row._type === "income") {
    const parent =
      (row.addressedTo && row.addressedTo.name) || row.parentName || "";
    const lines = (row.details || []).map((detail) => {
      const kind = feeKindLabel(detail, t);
      return [detail.studentName || "--", kind].filter(Boolean).join("\n");
    });
    if (parent) lines.push(`${t("recette.parent")}: ${parent}`);
    return lines.filter(Boolean).join("\n") || "--";
  }
  const name = row._beneficiaryName || "--";
  const label = row._label || "";
  return label ? `${name}\n${label}` : name;
};

const paymentMethodsOf = (row, t) => {
  if (row._type === "income") {
    const methods = Array.isArray(row.paymentMethod)
      ? row.paymentMethod
      : row.paymentMethod
      ? [row.paymentMethod]
      : [];
    return methods.map(
      (method) => t(`recette.paymentMethod.${method}`) || method
    );
  }
  if (row._matchedSplits && row._matchedSplits.length) {
    return row._matchedSplits.map(
      (sp) => t(`decaissement.paymentMethod.${sp.method}`) || sp.method
    );
  }
  if (row.methods && row.methods.length) {
    return row.methods.map((m) => t(`decaissement.paymentMethod.${m}`) || m);
  }
  return [];
};

const paymentMethodText = (row, t) => {
  const labels = paymentMethodsOf(row, t);
  return labels.length ? labels.join(", ") : "--";
};

const drawLegend = (doc, t, isArabic, pageWidth, y) => {
  const items = [
    { color: TYPE_COLORS.income, label: t("caisse.typeEncaissement") },
    { color: TYPE_COLORS.depense, label: t("caisse.typeDecaissement") },
    {
      color: TYPE_COLORS.inverseIncome,
      label: t("caisse.typeInverseEncaissement"),
    },
    { color: TYPE_COLORS.avoirCredit, label: t("caisse.typeAvoirCredit") },
  ];
  doc.setFontSize(8);
  let x = isArabic ? pageWidth - 19 : 15;
  items.forEach((item) => {
    doc.setFillColor(item.color[0], item.color[1], item.color[2]);
    doc.setDrawColor(200, 200, 200);
    if (isArabic) {
      doc.rect(x - 4, y, 4, 4, "FD");
      doc.text(item.label, x - 7, y + 3, { align: "right" });
      x -= 7 + doc.getTextWidth(item.label) + 10;
    } else {
      doc.rect(x, y, 4, 4, "FD");
      doc.text(item.label, x + 6, y + 3);
      x += 6 + doc.getTextWidth(item.label) + 10;
    }
  });
};

const addPageNumbers = (doc, t) => {
  const pages = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  for (let j = 1; j <= pages; j++) {
    doc.setPage(j);
    doc.text(
      t("pdf.pageNumber", { current: j, total: pages }),
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );
  }
};

const summarizeMethods = (rows, currency, t) => {
  const stats = {};
  let totalAbs = 0;
  rows.forEach((row) => {
    const amount = amountOf(row);
    const signed = isIncomeType(row._type) ? amount : -amount;
    totalAbs += Math.abs(signed);
    const labels = paymentMethodsOf(row, t);
    const keys = labels.length ? labels : ["--"];
    keys.forEach((label) => {
      if (!stats[label]) stats[label] = { quantity: 0, amount: 0 };
      stats[label].quantity += 1;
      stats[label].amount += signed / keys.length;
    });
  });
  const body = Object.entries(stats)
    .map(([method, s]) => {
      const pct =
        totalAbs > 0 ? ((Math.abs(s.amount) / totalAbs) * 100).toFixed(2) : "0.00";
      return [
        method,
        String(s.quantity),
        formatCurrency(s.amount, currency),
        `${pct}%`,
      ];
    })
    .sort((a, b) => {
      const amountA = Math.abs(parseFloat(String(a[2]).replace(/[^\d,-.]/g, "")));
      const amountB = Math.abs(parseFloat(String(b[2]).replace(/[^\d,-.]/g, "")));
      return amountB - amountA;
    });
  const totalQuantity = Object.values(stats).reduce(
    (sum, s) => sum + s.quantity,
    0
  );
  const totalAmount = Object.values(stats).reduce((sum, s) => sum + s.amount, 0);
  return { body, totalQuantity, totalAmount };
};

export const generateJournalCaissePdf = async ({
  data,
  settings,
  t,
  locale = "fr-FR",
  filters = {},
  schoolarYearNames = {},
}) => {
  const currency = getBuildingCurrency();
  const isArabic = locale === "ar-AR";
  const doc = new JsPDF("landscape");
  doc.setFont("Amiri");

  const pageWidth = doc.internal.pageSize.getWidth();
  const dateOptions = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const printDate = new Date().toLocaleDateString(locale, dateOptions);

  doc.setFontSize(8);
  const dateLine = `${t("caisse.dateImpression")} : ${printDate}`;
  if (isArabic) {
    doc.text(dateLine, pageWidth - 15, 8, { align: "right" });
  } else {
    doc.text(dateLine, 15, 8);
  }

  const logo = loadLogo(settings);
  try {
    if (logo && logo.src) {
      const logoX = isArabic ? pageWidth - 35 : 15;
      doc.addImage(logo, "JPEG", logoX, 11, 18, 18);
    }
  } catch (error) {
    console.log(error);
  }

  doc.setFontSize(15);
  doc.setFont("Amiri", "Bold");
  const title = t("caisse.journalCaisseTitle");
  doc.text(title, pageWidth / 2, 22, { align: "center" });
  doc.setFont("Amiri", "normal");

  const filterParts = [
    filters.caisseName
      ? `${t("encaissement.cashRegister")} : ${filters.caisseName}`
      : null,
    `${t("recette.anneeScolaire")} : ${
      filters.schoolarYearLabel || t("caisse.toutesLesAnnees")
    }`,
    filters.dateLabel || t("caisse.touteLaPeriode"),
  ].filter(Boolean);
  doc.setFontSize(10);
  const filterText = filterParts.join("  |  ");
  const filterLines = doc.splitTextToSize(filterText, pageWidth - 30);
  let y = 29;
  filterLines.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 5;
  });

  doc.setFontSize(9);
  doc.text(
    `${t("caisse.statutCollecte")} : ${filters.collecteLabel || "--"}`,
    pageWidth / 2,
    y,
    { align: "center" }
  );
  y += 8;

  drawLegend(doc, t, isArabic, pageWidth, y);
  y += 8;

  const activeRows = data.filter((row) => !isCancelledInvoice(row));
  const cancelledRows = data.filter(isCancelledInvoice);

  const rowColors = [];
  const body = activeRows.map((row) => {
    rowColors.push(TYPE_COLORS[row._type] || TYPE_COLORS.depense);
    const amount = amountOf(row);
    const signedLabel = isIncomeType(row._type)
      ? formatCurrency(amount, currency)
      : `-${formatCurrency(amount, currency)}`;
    return reverseIfArabic(
      [
        typeLabel(row, t),
        schoolarYearLabel(row, schoolarYearNames),
        serialOf(row),
        formatRowDate(row),
        descriptionOf(row, t),
        paymentMethodText(row, t),
        signedLabel,
        doneByOf(row),
      ],
      isArabic
    );
  });

  const totalRecettes = activeRows.reduce(
    (sum, row) => sum + (isIncomeType(row._type) ? amountOf(row) : 0),
    0
  );
  const totalDepenses = activeRows.reduce(
    (sum, row) => sum + (isIncomeType(row._type) ? 0 : amountOf(row)),
    0
  );
  const balance = totalRecettes - totalDepenses;

  const amountCol = isArabic ? 1 : 6;

  doc.autoTable({
    startY: y,
    styles: {
      font: "Amiri",
      fontSize: 7,
      valign: "middle",
      halign: "center",
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8,
    },
    bodyStyles: {
      lineWidth: 0.05,
      lineColor: [200, 200, 200],
    },
    alternateRowStyles: { fillColor: false },
    columnStyles: isArabic
      ? {
          2: { cellWidth: 28, overflow: "linebreak" },
          3: { cellWidth: 62, halign: "right", overflow: "linebreak" },
        }
      : {
          4: { cellWidth: 62, halign: "left", overflow: "linebreak" },
          6: { cellWidth: 28, overflow: "linebreak" },
        },
    head: [
      reverseIfArabic(
        [
          t("caisse.typeColonne"),
          t("caisse.anneeScolaireColonne"),
          t("recette.nSerie"),
          t("caisse.dateHeure"),
          t("caisse.descriptionColonne"),
          t("recette.modePaiement"),
          t("recette.montantTTC"),
          t("recette.doneBy"),
        ],
        isArabic
      ),
    ],
    body,
    foot: [
      reverseIfArabic(
        [
          {
            colSpan: 6,
            content: t("caisse.totalRecettes"),
            styles: { halign: "right", fontStyle: "bold" },
          },
          {
            content: formatCurrency(totalRecettes, currency),
            styles: { fontStyle: "bold", halign: "center" },
          },
          { content: "", styles: { fillColor: [255, 255, 255] } },
        ],
        isArabic
      ),
      reverseIfArabic(
        [
          {
            colSpan: 6,
            content: t("caisse.totalDepenses"),
            styles: { halign: "right", fontStyle: "bold" },
          },
          {
            content: formatCurrency(totalDepenses, currency),
            styles: { fontStyle: "bold", halign: "center" },
          },
          { content: "", styles: { fillColor: [255, 255, 255] } },
        ],
        isArabic
      ),
    ],
    didParseCell: (hook) => {
      if (hook.section === "body") {
        const color = rowColors[hook.row.index];
        if (color) hook.cell.styles.fillColor = color;
      }
      if (hook.section === "foot" && hook.column.index === amountCol) {
        hook.cell.styles.halign = "center";
      }
    },
  });

  if (cancelledRows.length) {
    const cancelledTotal = cancelledRows.reduce(
      (sum, row) =>
        sum +
        Number(
          (row.cancellation && row.cancellation.money) || row.montantTotal || 0
        ),
      0
    );
    const cancelledBody = cancelledRows.map((row) => {
      const cancelType =
        (row.cancellation && row.cancellation.type) || t("recette.annule");
      return reverseIfArabic(
        [
          serialOf(row),
          schoolarYearLabel(row, schoolarYearNames),
          formatRowDate(row),
          descriptionOf(row, t),
          cancelType,
          formatCurrency(
            (row.cancellation && row.cancellation.money) || amountOf(row),
            currency
          ),
          (row.cancellation && row.cancellation.cancelledBy) || "--",
          (row.cancellation && row.cancellation.note) || "--",
        ],
        isArabic
      );
    });

    doc.setFontSize(12);
    doc.setFont("Amiri", "Bold");
    doc.text(
      t("caisse.facturesAnnulees"),
      isArabic ? pageWidth - 15 : 15,
      doc.autoTableEndPosY() + 12,
      { align: isArabic ? "right" : "left" }
    );
    doc.setFont("Amiri", "normal");

    doc.autoTable({
      startY: doc.autoTableEndPosY() + 16,
      styles: {
        font: "Amiri",
        fontSize: 7,
        valign: "middle",
        halign: "center",
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [192, 57, 43],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8,
      },
      bodyStyles: {
        fillColor: [255, 241, 240],
        lineWidth: 0.05,
        lineColor: [200, 200, 200],
      },
      head: [
        reverseIfArabic(
          [
            t("recette.nSerie"),
            t("caisse.anneeScolaireColonne"),
            t("caisse.dateHeure"),
            t("caisse.descriptionColonne"),
            t("recette.type"),
            t("recette.montantTTC"),
            t("recette.cancelledBy"),
            t("paiement.note"),
          ],
          isArabic
        ),
      ],
      body: cancelledBody,
      foot: [
        reverseIfArabic(
          [
            {
              colSpan: 5,
              content: t("liste.total"),
              styles: { halign: "right", fontStyle: "bold" },
            },
            {
              content: formatCurrency(cancelledTotal, currency),
              styles: { fontStyle: "bold", halign: "center" },
            },
            { content: "" },
            { content: "" },
          ],
          isArabic
        ),
      ],
      didParseCell: (hook) => {
        if (hook.section !== "body") return;
        const row = cancelledRows[hook.row.index];
        const cancelType = row && row.cancellation && row.cancellation.type;
        hook.cell.styles.fillColor =
          cancelType === "Avoir" ? [255, 234, 153] : [255, 204, 204];
      },
    });
  }

  const afterMain = doc.autoTableEndPosY() + 12;
  doc.setFontSize(12);
  doc.setFont("Amiri", "Bold");
  doc.text(t("caisse.balanceTable"), isArabic ? pageWidth - 15 : 15, afterMain, {
    align: isArabic ? "right" : "left",
  });
  doc.setFont("Amiri", "normal");

  doc.autoTable({
    startY: afterMain + 4,
    styles: {
      font: "Amiri",
      valign: "middle",
      halign: "center",
      fontSize: 10,
    },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    bodyStyles: {
      lineWidth: 0.1,
      lineColor: [100, 100, 100],
    },
    head: [
      reverseIfArabic(
        [t("caisse.balanceTable"), t("recette.montant")],
        isArabic
      ),
    ],
    body: [
      reverseIfArabic(
        [t("caisse.totalEncaissements"), formatCurrency(totalRecettes, currency)],
        isArabic
      ),
      reverseIfArabic(
        [t("caisse.totalDecaissements"), formatCurrency(totalDepenses, currency)],
        isArabic
      ),
      reverseIfArabic(
        [t("caisse.balance"), formatCurrency(balance, currency)],
        isArabic
      ),
    ],
  });

  const summary = summarizeMethods(activeRows, currency, t);
  doc.setFontSize(12);
  doc.setFont("Amiri", "Bold");
  doc.text(
    t("encaissement.summaryByPaymentMethod"),
    isArabic ? pageWidth - 15 : 15,
    doc.autoTableEndPosY() + 12,
    { align: isArabic ? "right" : "left" }
  );
  doc.setFont("Amiri", "normal");

  doc.autoTable({
    startY: doc.autoTableEndPosY() + 16,
    styles: {
      font: "Amiri",
      valign: "middle",
      halign: "center",
      fontSize: 10,
    },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    bodyStyles: {
      lineWidth: 0.1,
      lineColor: [100, 100, 100],
    },
    head: [
      reverseIfArabic(
        [
          t("recette.modePaiement"),
          t("encaissement.quantite"),
          t("recette.montantTotal"),
          t("etablissement.pourcentage"),
        ],
        isArabic
      ),
    ],
    body: summary.body.map((row) => reverseIfArabic(row, isArabic)),
    foot: [
      reverseIfArabic(
        [
          {
            content: t("encaissement.totalGeneral"),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [52, 73, 94],
              textColor: [255, 255, 255],
            },
          },
          {
            content: String(summary.totalQuantity),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [52, 73, 94],
              textColor: [255, 255, 255],
            },
          },
          {
            content: formatCurrency(summary.totalAmount, currency),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [52, 73, 94],
              textColor: [255, 255, 255],
            },
          },
          {
            content: "100%",
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [52, 73, 94],
              textColor: [255, 255, 255],
            },
          },
        ],
        isArabic
      ),
    ],
  });

  addPageNumbers(doc, t);
  doc.save(`${t("caisse.journalCaisseTitle")}.pdf`);
};
